<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card>
            <card-header :title="title" />
            <v-card-text>
                <v-row>
                    <v-col v-if="!isCycleRule" cols="12" xs="12" sm="12" md="6">
                        <v-select
                            v-model="rule.rule_type"
                            :items="model.availableTypes"
                            :label="$t('resources.rule.attributes.type')"
                            item-text="name"
                            item-value="value"
                            :error-messages="typeErrors"
                            @change="$v.model.rule_type.$touch()"
                            @blur="$v.model.rule_type.$touch()"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-currency-field
                            v-if="rule.rule_type == 'revenue'"
                            v-model="rule.min_value"
                            :label="$t('resources.rule.attributes.min_value')"
                            :hint="$t('resources.rule.hints.min_value')"
                            :error-messages="minValueErrors"
                            @input="$v.model.min_value.$touch()"
                            @blur="$v.model.min_value.$touch()"
                        ></v-currency-field>

                        <v-text-field
                            v-if="rule.rule_type != 'revenue'"
                            type="number"
                            v-model="rule.min_value"
                            :label="
                                $t('resources.cycle_rule.attributes.min_value')
                            "
                            :hint="$t('resources.cycle_rule.hints.min_value')"
                            :error-messages="minValueErrors"
                            @input="$v.model.min_value.$touch()"
                            @blur="$v.model.min_value.$touch()"
                        />
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-currency-field
                            v-if="rule.rule_type == 'revenue'"
                            v-model="rule.max_value"
                            :label="$t('resources.rule.attributes.max_value')"
                            :hint="$t('resources.rule.hints.max_value')"
                            :error-messages="maxValueErrors"
                            @input="$v.model.max_value.$touch()"
                            @blur="$v.model.max_value.$touch()"
                        ></v-currency-field>

                        <v-text-field
                            v-if="rule.rule_type != 'revenue'"
                            type="number"
                            v-model="rule.max_value"
                            :label="
                                $t('resources.cycle_rule.attributes.max_value')
                            "
                            :hint="$t('resources.cycle_rule.hints.max_value')"
                            :error-messages="maxValueErrors"
                            @input="$v.model.max_value.$touch()"
                            @blur="$v.model.max_value.$touch()"
                        />
                    </v-col>
                    <v-col v-if="isCycleRule" cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            v-model="rule.percentage"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            :label="
                                $t('resources.cycle_rule.attributes.percentage')
                            "
                            :hint="$t('resources.cycle_rule.hints.percentage')"
                            :error-messages="percentageErrors"
                            @input="$v.model.percentage.$touch()"
                            @blur="$v.model.percentage.$touch()"
                            suffix="%"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import Rule from '@/models/Rule'
import CycleRule from '@/models/CycleRule'
import FormActions from '@/shared/components/FormActions'
import { validationMixin } from 'vuelidate'
import { required, between } from 'vuelidate/lib/validators'

export default {
    props: {
        titlePrefix: {
            type: String,
            default: '',
        },
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof Rule || model instanceof CycleRule
            },
        },
    },

    components: {
        FormActions,
    },

    mixins: [validationMixin],

    data: () => {
        return {
            loading: false,
        }
    },

    validations() {
        const validations = {
            model: {
                min_value: {
                    between: between(0, 999999.99),
                },
                max_value: {
                    between: between(0, 999999.99),
                },
                rule_type: { required },
                percentage: {
                    between: between(0, 100),
                },
            },
        }

        // Para regras de ciclo, percentage é obrigatório
        if (this.isCycleRule) {
            validations.model.percentage.required = required
        }

        return validations
    },

    computed: {
        title() {
            const resourceKey = this.isCycleRule ? 'cycle_rule' : 'rule'
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc(
                    `resources.${resourceKey}.name`,
                    1,
                ).toLowerCase(),
            })
        },

        rule() {
            return this.model
        },

        category_id() {
            return this.$route.params.category_id
        },

        isCycleRule() {
            return (
                this.$route.query.cycle_rule === 'true' ||
                this.$route.query.cycle_rule === '1'
            )
        },

        minValueErrors() {
            const errors = []
            if (!this.$v.model.min_value.$dirty) return errors

            const label = this.$t(
                'resources.rule.attributes.min_value',
            ).toLowerCase()

            !this.$v.model.min_value.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: label,
                        min: '0.00',
                        max: '999999.99',
                    }),
                )

            return errors
        },

        maxValueErrors() {
            const errors = []
            if (!this.$v.model.max_value.$dirty) return errors

            const label = this.$t(
                'resources.rule.attributes.max_value',
            ).toLowerCase()

            !this.$v.model.max_value.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: label,
                        min: '0.00',
                        max: '999999.99',
                    }),
                )

            return errors
        },

        typeErrors() {
            const errors = []
            if (!this.$v.model.rule_type.$dirty) return errors

            const label = this.$t(
                'resources.rule.attributes.type',
            ).toLowerCase()

            !this.$v.model.rule_type.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: label,
                    }),
                )

            return errors
        },

        percentageErrors() {
            const errors = []
            if (!this.$v.model.percentage.$dirty) return errors

            const label = this.$t(
                'resources.rule.attributes.percentage',
            ).toLowerCase()

            !this.$v.model.percentage.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: label,
                        min: '0',
                        max: '100',
                    }),
                )

            // Para regras de ciclo, percentage é obrigatório
            if (
                this.isCycleRule &&
                this.$v.model.percentage.required !== undefined
            ) {
                !this.$v.model.percentage.required &&
                    errors.push(
                        this.$t('forms.errors.required', {
                            label: label,
                        }),
                    )
            }

            return errors
        },
    },

    created() {
        // Configurar valores para regras de ciclo
        if (this.isCycleRule) {
            this.rule.rule_type = 'customer'
            this.rule.cycle_rule = true
        }
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.loading = false
                return
            }

            try {
                this.rule.category_id = this.category_id

                // Garantir valores para regras de ciclo
                if (this.isCycleRule) {
                    this.rule.rule_type = 'customer'
                    this.rule.cycle_rule = true
                }

                await this.rule.save()
            } finally {
                this.loading = false
            }

            const resourceKey = this.isCycleRule ? 'cycle_rule' : 'rule'
            this.$bus.$emit(
                'notify',
                'success',
                this.$t('resources.feedbacks.success.save', {
                    resource: this.$tc(`resources.${resourceKey}.name`, 1),
                }),
            )

            this.$emit('saved')
        },
    },
}
</script>

<style></style>
